<template>
  <div class="ci-card" :style="cssVars">

    <!-- ── Processando ─────────────────────────────────────────── -->
    <div v-if="flow === 'processing'" class="ci-card__state-box">
      <div class="ci-card__spinner">
        <span class="spinner-border"></span>
      </div>
      <p class="ci-card__processing-label">{{ processingMsg }}</p>
    </div>

    <!-- ── Sucesso ─────────────────────────────────────────────── -->
    <div v-else-if="flow === 'success'" class="ci-card__state-box ci-card__state-box--success">
      <i class="fas fa-check-circle ci-card__success-icon"></i>
      <h3 class="ci-card__success-title">Pagamento confirmado!</h3>
      <p class="ci-card__success-sub">Seu presente foi registrado com sucesso 🎉</p>
    </div>

    <!-- ── Formulário ──────────────────────────────────────────── -->
    <template v-else>
      <!-- Valor a pagar -->
      <div class="ci-card__amount-block">
        <span class="ci-card__amount-label">Valor a pagar</span>
        <span class="ci-card__amount-value">R$ {{ formattedTotal }}</span>
      </div>

      <!-- Stripe Card Element -->
      <div class="ci-card__field-wrap">
        <label class="ci-card__label">Dados do cartão</label>
        <div id="ci-stripe-card-element" class="ci-card__stripe-element"></div>
        <span v-if="stripeCardError" class="ci-card__error">{{ stripeCardError }}</span>
      </div>

      <!-- Erro de pagamento -->
      <div v-if="paymentError" class="ci-card__payment-error">
        <i class="fas fa-circle-exclamation me-2"></i>
        {{ paymentError }}
      </div>

      <!-- Parcelamento -->
      <InstallmentCalculator
        :bin="cardBin"
        :total="props.total"
        :disabled="sending"
        :allow-without-bin="true"
        :initial-selected-installment="selectedInstallment"
        @update:selected-installment="inst => selectedInstallment = inst"
      />

      <!-- Info parcela selecionada -->
      <div v-if="selectedInstallment && selectedInstallment.number > 1" class="ci-card__installment-info">
        <i class="fas fa-info-circle me-1"></i>
        Total a pagar: <strong>R$ {{ formatCurrency(selectedInstallment.total_value) }}</strong>
      </div>

      <!-- Botão pagar -->
      <button
        class="ci-card__submit"
        :disabled="sending || !stripeCardComplete"
        @click="pay"
      >
        <span v-if="sending" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="fas fa-lock me-2"></i>
        {{ sending ? 'Processando…' : `Pagar ${selectedInstallment ? `${selectedInstallment.number}x` : ''} com Cartão` }}
      </button>

      <!-- Voltar -->
      <button class="ci-card__back-btn" @click="$emit('back')" :disabled="sending">
        <i class="fas fa-chevron-left me-1"></i> Voltar
      </button>

      <p class="ci-card__security-note">
        <i class="fas fa-shield-halved me-1"></i>
        Ambiente 100% seguro e criptografado
      </p>
    </template>

    <!-- ── Modal cartão recusado ───────────────────────────────── -->
    <CardDeclinedModal
      :show="showDeclinedModal"
      @try-again="handleTryAgain"
      @use-pix="$emit('use-pix')"
    />

  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'vue3-toastify';
import api from '@/services/api';
import { formatCurrency } from '@/utils/currencyFormatter';
import InstallmentCalculator from '@/components/payment/InstallmentCalculator.vue';
import CardDeclinedModal from '@/components/payment/CardDeclinedModal.vue';

const props = defineProps({
  eventId:  { type: String, required: true },
  customer: { type: Object, required: true },
  items:    { type: Array,  default: () => [] },
  total:    { type: Number, default: 0 },
  primaryColor: { type: String, default: '#ff50a3' },
});

const emit = defineEmits(['success', 'back', 'use-pix', 'state-change']);

// ── CSS vars ───────────────────────────────────────────────────────
const cssVars = computed(() => ({ '--ci-primary': props.primaryColor }));
const formattedTotal = computed(() => formatCurrency(props.total));

// ── State ──────────────────────────────────────────────────────────
const flow           = ref('form'); // 'form' | 'processing' | 'success'
const sending        = ref(false);
const processingMsg  = ref('Processando pagamento…');
const paymentError   = ref('');
const showDeclinedModal = ref(false);

// Stripe
const stripeClient      = ref(null);
const stripeElements    = ref(null);
const stripeCardElement = ref(null);
const stripeCardComplete = ref(false);
const stripeCardError    = ref('');
const stripePublishableKey = ref('');
const cardBin = ref('');

// Parcelamento
const selectedInstallment = ref(null);

watch(flow, (value) => {
  emit('state-change', value);
}, { immediate: true });

// Polling
let pollInterval = null;
let pollAttempts = 0;
const MAX_POLL = 40;

// ── Init ───────────────────────────────────────────────────────────
onMounted(async () => {
  await loadConfig();
  await mountStripeElement();
});

onBeforeUnmount(() => {
  destroyStripe();
  clearPolling();
});

async function loadConfig() {
  try {
    const { data } = await api.get('/config/fees');
    stripePublishableKey.value = data.stripe_publishable_key || '';
  } catch {
    toast.error('Erro ao carregar configurações de pagamento.');
  }
}

async function mountStripeElement() {
  if (!stripePublishableKey.value) return;

  stripeClient.value = await loadStripe(stripePublishableKey.value);
  stripeElements.value = stripeClient.value.elements();

  const el = stripeElements.value.create('card', {
    hidePostalCode: true,
    disableLink: true,
    style: {
      base: {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'Nunito, sans-serif',
        backgroundColor: 'transparent',
        '::placeholder': { color: 'rgba(255,255,255,0.4)' },
      },
      invalid: { color: '#ff6b6b' },
    },
  });

  el.mount('#ci-stripe-card-element');

  el.on('change', (event) => {
    stripeCardComplete.value = !!event.complete;
    stripeCardError.value    = event.error?.message || '';

    // Extrair BIN para cálculo de parcelas
    if (event.value?.postalCode !== undefined || event.elementType) {
      // Stripe não expõe o BIN diretamente — usamos allowWithoutBin=true
    }
  });

  stripeCardElement.value = el;
}

function destroyStripe() {
  if (stripeCardElement.value) {
    stripeCardElement.value.destroy();
    stripeCardElement.value = null;
  }
  stripeElements.value = null;
  stripeCardComplete.value = false;
  stripeCardError.value = '';
}

async function remountStripeElement() {
  destroyStripe();
  await mountStripeElement();
}

// ── Pay ────────────────────────────────────────────────────────────
async function pay() {
  if (!stripeCardComplete.value || sending.value) return;

  sending.value = true;
  processingMsg.value = 'Processando pagamento…';
  paymentError.value = '';

  let token = '';
  try {
    const { token: t, error } = await stripeClient.value.createToken(stripeCardElement.value, {
      name: props.customer.name,
    });
    if (error) throw new Error(error.message);
    token = t?.id || '';
  } catch (err) {
    toast.error(err.message || 'Erro ao tokenizar o cartão.');
    sending.value = false;
    return;
  }

  const installment = selectedInstallment.value;
  const payTotal = installment ? installment.total_value : props.total;

  const payload = {
    event_id:          props.eventId,
    items:             props.items.map(i => ({ id: i.id, price: i.price, quantity: i.quantity || 1 })),
    total:             payTotal,
    method:            'card',
    customer_name:     props.customer.name,
    customer_cpf_cnpj: props.customer.cpf_cnpj,
    customer_email:    props.customer.email,
    customer_phone:    props.customer.phone || '',
    customer_postal_code:    props.customer.postal_code || '',
    customer_address_number: props.customer.address_number || '',
    customer: {
      name:           props.customer.name,
      cpf_cnpj:       props.customer.cpf_cnpj,
      email:          props.customer.email,
      phone:          props.customer.phone || '',
      postal_code:    props.customer.postal_code || '',
      address_number: props.customer.address_number || '',
    },
    card: {
      credit_card_token: token,
      installment: installment?.number || 1,
    },
    installment_count: installment?.number || 1,
    installment_value: installment?.installment_value || payTotal,
  };

  try {
    flow.value = 'processing';
    const { data } = await api.post(`/payments/card/${props.eventId}`, payload);

    if (data.status === 'paid') {
      onSuccess();
      return;
    }

    // 3DS necessário
    if (data.stripe_client_secret && stripeClient.value) {
      processingMsg.value = 'Aguardando autenticação do banco…';
      const { error } = await stripeClient.value.handleNextAction({ clientSecret: data.stripe_client_secret });
      if (error) {
        onDeclined();
        return;
      }
    }

    if (['processing', 'authorized', 'waiting_payment'].includes(data.status)) {
      startPolling(data.id);
      return;
    }

    onDeclined();
  } catch (err) {
    const serverMsg = err?.response?.data?.message || '';
    const isCardError = /cartão|card|cvv|número|validade|token|autoriza|recusad/i.test(serverMsg);
    paymentError.value = serverMsg
      ? (isCardError ? serverMsg : serverMsg)
      : 'Não foi possível processar o pagamento. Verifique os dados do cartão e tente novamente.';
    flow.value = 'form';
    sending.value = false;
    await remountStripeElement();
  }
}

// ── Polling ────────────────────────────────────────────────────────
function startPolling(paymentId) {
  pollAttempts = 0;
  clearPolling();

  pollInterval = setInterval(async () => {
    pollAttempts++;
    if (pollAttempts > MAX_POLL) {
      clearPolling();
      flow.value = 'form';
      sending.value = false;
      toast.warning('Tempo esgotado. Verifique seu e-mail para confirmação.');
      return;
    }

    try {
      const { data } = await api.get(`/payments/status/${paymentId}`);
      if (data.status === 'paid' || data.status === 'confirmed') {
        clearPolling();
        onSuccess();
      } else if (['failed', 'declined', 'refused', 'canceled'].includes(data.status)) {
        clearPolling();
        onDeclined();
      }
    } catch { /* continua polling */ }
  }, 5000);
}

function clearPolling() {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
}

// ── Resultados ─────────────────────────────────────────────────────
function onSuccess() {
  flow.value = 'success';
  sending.value = false;
  emit('success');
}

function onDeclined() {
  flow.value = 'form';
  sending.value = false;
  paymentError.value = '';
  showDeclinedModal.value = true;
}

function handleTryAgain() {
  showDeclinedModal.value = false;
  remountStripeElement();
}
</script>

<style scoped>
/* ── Erro de pagamento ── */
.ci-card__payment-error {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  background: rgba(255, 107, 107, 0.12);
  border: 1px solid rgba(255, 107, 107, 0.35);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  color: #ff9090;
  font-size: 0.85rem;
  line-height: 1.4;
}
.ci-card__payment-error i {
  flex-shrink: 0;
  margin-top: 0.1rem;
  color: #ff6b6b;
}

/* ── Layout ── */
.ci-card {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
}

/* ── Valor ── */
.ci-card__amount-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  margin-bottom: 0.25rem;
}
.ci-card__amount-label {
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.5);
}
.ci-card__amount-value {
  font-size: 2rem;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.02em;
}

/* ── Stripe Element ── */
.ci-card__field-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.ci-card__label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.6);
}
.ci-card__stripe-element {
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 12px;
  padding: 1rem 1rem;
  transition: border-color 0.2s;
}
.ci-card__stripe-element:focus-within {
  border-color: var(--ci-primary);
}
.ci-card__error {
  font-size: 0.78rem;
  color: #ff6b6b;
}

/* ── Info parcela ── */
.ci-card__installment-info {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.55);
  text-align: center;
  padding: 0.5rem;
  border-radius: 8px;
  background: rgba(255,255,255,0.05);
}

/* ── Botão pagar ── */
.ci-card__submit {
  width: 100%;
  padding: 0.9rem 1.5rem;
  border: none;
  border-radius: 14px;
  background: var(--ci-primary);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ci-card__submit:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.ci-card__submit:not(:disabled):active {
  transform: scale(0.98);
}

/* ── Voltar ── */
.ci-card__back-btn {
  background: none;
  border: none;
  color: rgba(255,255,255,0.5);
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  align-self: flex-start;
  transition: color 0.2s;
}
.ci-card__back-btn:hover { color: rgba(255,255,255,0.8); }

/* ── Segurança ── */
.ci-card__security-note {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.3);
  text-align: center;
  margin: 0;
}

/* ── Estados ── */
.ci-card__state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 1rem;
  text-align: center;
}
.ci-card__spinner .spinner-border {
  width: 2.5rem;
  height: 2.5rem;
  border-width: 3px;
  color: var(--ci-primary);
}
.ci-card__processing-label {
  color: rgba(255,255,255,0.7);
  font-size: 0.95rem;
}
.ci-card__success-icon {
  font-size: 3rem;
  color: #22c55e;
}
.ci-card__success-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
}
.ci-card__success-sub {
  color: rgba(255,255,255,0.6);
  font-size: 0.9rem;
  margin: 0;
}

/* ── Override selects do InstallmentCalculator ── */
.ci-card :deep(.form-label) {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.6);
  margin-bottom: 0.4rem;
}
.ci-card :deep(.form-select) {
  background-color: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 12px;
  color: #fff;
  font-size: 0.92rem;
  padding: 0.75rem 1rem;
}
.ci-card :deep(.form-select:focus) {
  border-color: var(--ci-primary);
  box-shadow: none;
  background-color: rgba(255,255,255,0.1);
}
.ci-card :deep(.form-select option) {
  background: #1e1e2e;
  color: #fff;
}
</style>
